import {
  DAY_START_MIN,
  PX_PER_MIN,
  formatRange,
  minutesToHHMM,
  type ScheduleLesson,
} from '@skolveckan/shared';
import { subjectColors } from '../lib/subjects';
import { IconCheck } from './Icons';

export type LessonPhase = 'past' | 'current' | 'upcoming';

export function phaseOf(lesson: ScheduleLesson, nowMin: number, isToday: boolean): LessonPhase {
  if (!isToday) return 'upcoming';
  if (nowMin >= lesson.endMin) return 'past';
  if (nowMin >= lesson.startMin) return 'current';
  return 'upcoming';
}

/** y-position och höjd i px. Hela timvyns matematik bor här. */
export function blockGeometry(lesson: ScheduleLesson) {
  return {
    top: (lesson.startMin - DAY_START_MIN) * PX_PER_MIN,
    height: (lesson.endMin - lesson.startMin) * PX_PER_MIN,
  };
}

interface Props {
  lesson: ScheduleLesson;
  phase: LessonPhase;
  progress?: number;
  compact?: boolean;
  onOpen?: (lesson: ScheduleLesson) => void;
}

export function LessonBlock({ lesson, phase, progress, compact, onOpen }: Props) {
  const { top, height } = blockGeometry(lesson);
  const colors = subjectColors(lesson.subject?.colorKey ?? 'break');
  const style = { top, height: Math.max(height - 2, 14) };

  if (lesson.kind !== 'lesson') {
    return (
      <div className="block block--break" style={style}>
        <span className="block__breaklabel">
          {lesson.title ?? (lesson.kind === 'lunch' ? 'Lunch' : 'Rast')}{' '}
          {formatRange(lesson.startMin, lesson.endMin)}
        </span>
        {lesson.room && !compact && <span className="block__breakroom">{lesson.room}</span>}
      </div>
    );
  }

  const isCurrent = phase === 'current';
  const blockStyle = {
    ...style,
    background: isCurrent ? 'var(--surface)' : colors.fill,
    borderColor: isCurrent ? colors.line : 'transparent',
    borderLeftColor: colors.line,
    opacity: phase === 'past' ? 0.6 : 1,
  };

  return (
    <button
      type="button"
      className={`block block--lesson${isCurrent ? ' block--current' : ''}`}
      style={blockStyle}
      onClick={() => onOpen?.(lesson)}
      aria-label={`${lesson.subject?.name ?? 'Lektion'} ${formatRange(lesson.startMin, lesson.endMin)}${
        lesson.title ? `, ${lesson.title}` : ''
      }`}
    >
      <span className="block__head">
        <span className="block__subject" style={{ color: colors.ink }}>
          {lesson.subject?.name ?? 'Lektion'}
        </span>
        {isCurrent && <span className="chip chip--now">NU</span>}
        {!compact && (
          <span className="block__time">
            {formatRange(lesson.startMin, lesson.endMin)}
            {lesson.room ? ` · ${lesson.room.replace('Sal ', '')}` : ''}
          </span>
        )}
      </span>

      {compact ? (
        <span className="block__time block__time--compact">
          {minutesToHHMM(lesson.startMin)}
          {lesson.room ? ` · ${lesson.room.replace('Sal ', '')}` : ''}
        </span>
      ) : (
        <span className="block__title">
          {lesson.title ?? <span className="block__title--empty">Inget innehåll än</span>}
        </span>
      )}

      {lesson.assignmentCount > 0 && !isCurrent && (
        <span
          className="block__badge"
          style={{ background: colors.line }}
          aria-label={`${lesson.assignmentCount} uppgifter`}
        >
          {lesson.assignmentCount}
        </span>
      )}

      {lesson.hasExam && <span className="block__exam">PROV</span>}

      {isCurrent && typeof progress === 'number' && (
        <span className="block__progress" style={{ background: colors.fill }}>
          <span
            className="block__progressfill"
            style={{ width: `${Math.round(progress * 100)}%`, background: colors.line }}
          />
        </span>
      )}

      {phase === 'past' && lesson.assignmentCount === 0 && (
        <span className="block__done" aria-hidden="true">
          <IconCheck size={11} />
        </span>
      )}
    </button>
  );
}
